import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap, catchError, mergeMap, tap } from 'rxjs/operators';
import { CAPTURE_SESSIONS_SERVICE } from '../../services/capture-sessions/capture-sessions.service.base';
import { ICaptureSessionsService } from '../../interfaces/services/capture-sessions-service.interface';
import { getErrorMessage } from '../../utils/error.utils';
import * as CapturesActions from '../captures/captures.actions';
import { ToastService } from '../../services/toast.service';
import * as CaptureSessionsActions from './capture-sessions.actions';

@Injectable()
export class CaptureSessionsEffects {
  private actions$ = inject(Actions);
  private service = inject<ICaptureSessionsService>(CAPTURE_SESSIONS_SERVICE);
  private toastService = inject(ToastService);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaptureSessionsActions.loadCaptureSessions),
      switchMap(({ floorId }) =>
        this.service.getCaptureSessions(floorId).pipe(
          map((sessions) => CaptureSessionsActions.loadCaptureSessionsSuccess({ sessions })),
          catchError((error) => of(CaptureSessionsActions.loadCaptureSessionsFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  add$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaptureSessionsActions.addCaptureSession),
      mergeMap(({ floorId, captureSessionName, camera, device, startedAt }) =>
        this.service.addCaptureSession({ floorId, captureSessionName, camera, device, startedAt }).pipe(
          map((session) => CaptureSessionsActions.addCaptureSessionSuccess({ session })),
          catchError((error) => of(CaptureSessionsActions.addCaptureSessionFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  refreshCaptureSessionsAfterCaptureAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapturesActions.addCaptureSuccess),
      map(({ floorId }) => CaptureSessionsActions.loadCaptureSessions({ floorId }))
    )
  );

  refreshCaptureSessionsAfterAddSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaptureSessionsActions.addCaptureSessionSuccess),
      map(({ session }) => CaptureSessionsActions.loadCaptureSessions({ floorId: session.floorId }))
    )
  );

  addCaptureSessionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CaptureSessionsActions.addCaptureSessionFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );
}
